import { useCallback, useMemo, useState } from 'react';

import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import Grid from '@mui/material/Grid2';
import Paper from '@mui/material/Paper';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import PageLayout from 'src/components/PageLayout';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';

import DataTable from 'src/components/DataTable';
import ConfirmationDialog from 'src/components/ConfirmationDialog';

import messages from './messages';
import DynamicFieldForm from './components/DynamicFieldForm';
import { useDeleteDynamicField, useGetFieldCategory } from './api';
import { useGetDynamicFields } from './api/queries/useGetDynamicFields';

const textWrapSX = {
  wordBreak: 'break-word',
  overflowWrap: 'break-word',
};

export default function DynamicFieldsPage() {
  const { formatMessage } = useIntl();
  const { uuid } = useParams<{ uuid: string }>();

  const [params, setParams] = useState<Record<string, string | number>>({
    page: 1,
  });

  const formatBooleanField = useCallback((value: boolean) => {
    return value
      ? formatMessage(messages.yesLabel)
      : formatMessage(messages.noLabel);
  }, []);

  const { mutate: deleteDynamicField } = useDeleteDynamicField();
  const { data: fieldCategory } = useGetFieldCategory(uuid ?? '', {
    enabled: Boolean(uuid),
  });
  const { data: dynamicFields, isFetching: isFetchingDynamicFields } =
    useGetDynamicFields(uuid ?? '', params, {
      enabled: Boolean(uuid),
    });

  const columns: Array<{
    id: keyof UCM.ExtendedDynamicFieldType | string;
    label: string | React.ReactNode;
  }> = useMemo(
    () => [
      {
        id: 'nameEn',
        label: formatMessage(messages.nameEnColumnLabel),
        render: (nameEn: string) => {
          return <Typography variant="inherit">{nameEn}</Typography>;
        },
      },
      {
        id: 'nameDe',
        label: formatMessage(messages.nameDeColumnLabel),
        render: (nameDe: string) => {
          return <Typography variant="inherit">{nameDe}</Typography>;
        },
      },
      {
        id: 'type',
        align: 'center',
        label: formatMessage(messages.typeColumnLabel),
        render: (type: UCM.ExtendedDynamicFieldType['type']) => {
          return <Typography variant="inherit">{type.inputType}</Typography>;
        },
      },
      {
        id: 'position',
        label: formatMessage(messages.positionColumnLabel),
        align: 'center',
        render: (position: number) => {
          return <Typography variant="inherit">{position}</Typography>;
        },
      },
      {
        id: 'options',
        label: formatMessage(messages.optionsColumnLabel),
        align: 'center',
        render: (options: Array<UCM.ExtendedDynamicFieldType['options']>) => {
          return <Typography variant="inherit">{options.length}</Typography>;
        },
      },
      {
        id: 'questionEn',
        label: formatMessage(messages.questionEnColumnLabel),
        render: (questionEn: string) => {
          return <Typography variant="inherit">{questionEn}</Typography>;
        },
      },
      {
        id: 'questionDe',
        label: formatMessage(messages.questionDeColumnLabel),
        render: (questionDe: string) => {
          return <Typography variant="inherit">{questionDe}</Typography>;
        },
      },
      {
        id: 'isVisibleByAdmin',
        label: formatMessage(messages.visibleByAdminColumnLabel),
        align: 'center',
        render: (isVisibleByAdmin: boolean) => {
          return (
            <Typography variant="inherit">
              {formatBooleanField(isVisibleByAdmin)}
            </Typography>
          );
        },
      },
      {
        id: 'isVisibleByUser',
        label: formatMessage(messages.visibleByUserColumnLabel),
        align: 'center',
        render: (isVisibleByUser: boolean) => {
          return (
            <Typography variant="inherit">
              {formatBooleanField(isVisibleByUser)}
            </Typography>
          );
        },
      },
      {
        id: 'actions',
        label: '',
        align: 'center',
        width: 0,
        render: (_: Object, dynamicField: UCM.ExtendedDynamicFieldType) => (
          <Grid display="flex" alignItems="center">
            <DynamicFieldForm create={false} dynamicField={dynamicField} />

            <ConfirmationDialog
              onConfirm={() => handleDelete(dynamicField.uuid)}
              message={formatMessage(messages.deleteDynamicFieldConfirmMessage)}
            >
              {(handleOpen) => (
                <Tooltip arrow title={formatMessage(messages.deleteTooltip)}>
                  <IconButton onClick={handleOpen}>
                    <DeleteForeverOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </ConfirmationDialog>
          </Grid>
        ),
      },
    ],
    [],
  );

  const handleDelete = useCallback((uuid: string) => {
    deleteDynamicField(uuid);
  }, []);

  const handlePagination = useCallback((_: unknown, page: number) => {
    setParams({ page: page + 1 });
  }, []);

  return (
    <PageLayout backHref="/setup">
      {fieldCategory && (
        <Grid container spacing={2}>
          <Typography variant="h4">
            {formatMessage(messages.fieldCategoryDetailsSectionTitle)}
          </Typography>

          <Grid container size={12} component={Paper} px={1} py={2} spacing={1}>
            <DetailItem
              value={fieldCategory.name.en}
              label={formatMessage(messages.nameEnLabel)}
            />
            <DetailItem
              value={fieldCategory.name.de}
              label={formatMessage(messages.nameDeLabel)}
            />
            <DetailItem
              value={fieldCategory.position.toString()}
              label={formatMessage(messages.positionLabel)}
            />
          </Grid>
        </Grid>
      )}

      <Grid container size={12} mt={5} spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Typography variant="h4">
            {formatMessage(messages.dynamicFieldSectionTitle)}
          </Typography>
        </Grid>

        <Grid
          container
          alignItems="center"
          size={{ xs: 12, sm: 6 }}
          justifyContent={{ xs: 'flex-start', sm: 'flex-end' }}
        >
          <DynamicFieldForm create={true} />
        </Grid>

        <Grid container size={12}>
          <DataTable
            columns={columns}
            rows={dynamicFields?.data}
            onPaginate={handlePagination}
            pagination={dynamicFields?.meta}
            loading={isFetchingDynamicFields}
          />
        </Grid>
      </Grid>
    </PageLayout>
  );
}

function DetailItem({ label, value }: { label: string; value: string }) {
  return (
    <Grid
      size={{ xs: 12, sm: 6, md: 4 }}
      container
      spacing={1}
      display="flex"
      flexDirection="row"
    >
      <Typography variant="body1" color="textSecondary">
        {label}
      </Typography>
      <Typography
        sx={textWrapSX}
        variant="body1"
        color="primary"
        fontWeight="bold"
      >
        {value}
      </Typography>
    </Grid>
  );
}
